import { Box, useTheme } from '@mui/material'
import { FC, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import Attribute from './Attribute'
import { ICartItemRisk, IRiskFunction } from '../types'
import { useRiskFunctions } from 'hooks'
import { RiskIndicator } from 'packages/eid-ui'
import { Skeleton } from '@mui/material'

export interface ViolationDetailsProps {
    risk: ICartItemRisk
}

const ViolationDetails: FC<ViolationDetailsProps> = (props) => {
    const { risk } = props
    const theme = useTheme()
    const {
        data: leftSideFunctions,
        isInitialLoading: isInitialLoadingLeftSideFunctions,
    } = useRiskFunctions(
        risk.localRiskId,
        risk.assigneeId,
        true,
        risk.riskDetailsInversed,
    )

    const {
        data: rightSideFunctions,
        isInitialLoading: isInitialLoadingRightSideFunctions,
    } = useRiskFunctions(
        risk.localRiskId,
        risk.riskType === 'SegregationOfDuties'
            ? risk.riskReason_AssigneeId
            : '',
        false,
        risk.riskDetailsInversed,
    )

    const { t } = useTranslation()

    const renderRiskFunctions = (functions: IRiskFunction[]) => {
        if (functions.length === 0) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '12px',
                        marginRight: '24px',
                    }}
                >
                    {t('Common_NoDataFound')}
                </Box>
            )
        }
        return (
            <Box>
                {functions.map((rf) => (
                    <Box
                        key={rf.id}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '12px',
                            marginRight: '24px',
                        }}
                    >
                        <Box sx={{ lineHeight: 1.33, width: '240px' }}>
                            {rf.friendlyName}
                        </Box>
                        <Box sx={{ minWidth: '80px' }}>
                            <RiskIndicator
                                variant={rf.riskLevel}
                                label={t(`Common_${rf.riskLevel}Risk`)}
                                renderAsText
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
        )
    }

    const renderFunctionsGranted = (loading: boolean, riskFunctions: any) => {
        if (loading) {
            return (
                <>
                    {[1, 2].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rectangular"
                            width={'100%'}
                            height={20}
                            sx={{ marginTop: '10px' }}
                        />
                    ))}
                </>
            )
        } else {
            return <Fragment>{renderRiskFunctions(riskFunctions)}</Fragment>
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '16px 24px',
            }}
        >
            <Box
                sx={{
                    padding: '8px  0px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: theme?.palette?.common?.black,
                }}
            >
                {t('Common_ViolationDetails')}
            </Box>

            <Attribute
                label={t('Common_RiskType')}
                value={t(`Common_${risk.riskType}`)}
            />
            <Attribute
                label={t('Common_Description')}
                value={risk.description}
            />
            <Attribute
                label={t(
                    risk.riskType === 'SegregationOfDuties'
                        ? 'Common_FunctionsLeft'
                        : 'Common_Functions',
                )}
                value={
                    <Box sx={{ display: 'flex', marginTop: '-12px' }}>
                        {renderFunctionsGranted(
                            isInitialLoadingLeftSideFunctions,
                            leftSideFunctions,
                        )}
                    </Box>
                }
            />
            {risk.riskType === 'SegregationOfDuties' && (
                <Attribute
                    label={t('Common_FunctionsRight')}
                    value={
                        <Box sx={{ display: 'flex', marginTop: '-12px' }}>
                            {renderFunctionsGranted(
                                isInitialLoadingRightSideFunctions,
                                rightSideFunctions,
                            )}
                        </Box>
                    }
                />
            )}
        </Box>
    )
}

export default ViolationDetails
